<template>
  <div class="w-full flex flex-col gap-1 fade-in">
    <!-- Logged in user info -->
    <BaseText
      type="body"
      size="sm"
      class="text-text-normal text-center mb-2"
    >
      You're logged in as <span class="font-semibold">{{ userEmail }}</span>
    </BaseText>

    <div class="w-full flex flex-col gap-3">
      <!-- Accept Button -->
      <button
        class="submit-btn w-full py-2.5 px-3 rounded-md flex items-center justify-center"
        :disabled="loadingAccept || loadingDecline"
        @click="acceptInvite"
      >
        <BaseLoadingSpinnerCircle
          v-if="loadingAccept"
          class="text-white"
        />
        <BaseText
          v-else
          type="label"
          size="sm"
          class="text-white"
        >
          Accept & Join Team
        </BaseText>
      </button>

      <!-- Decline Button -->
      <button
        class="base-btn w-full py-2.5 px-3 rounded-md transition duration-100 flex items-center justify-center"
        :disabled="loadingAccept || loadingDecline"
        @click="declineInvite"
      >
        <BaseLoadingSpinnerCircle
          v-if="loadingDecline"
          class="text-text-muted"
        />
        <BaseText
          v-else
          type="label"
          size="sm"
          class="text-text-muted"
        >
          Decline
        </BaseText>
      </button>
    </div>
    <div class="mt-2 flex items-center justify-center">
      <BaseText
        type="body"
        size="sm"
        class="text-text-subdued text-center underline cursor-pointer w-max"
        @click.native="$emit('logout')"
      >
        Logout
      </BaseText>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'
import CloudFunctionsAPI from '@/api'
import firebase from '@/api/config/FirebaseConfig'

export default {
  name: 'TeamInviteAcceptDecline',
  props: {
    inviteId: {
      type: String,
      required: true
    },
    invite: {
      type: Object,
      required: true
    },
    userEmail: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loadingAccept: false,
      loadingDecline: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser'])
  },
  methods: {
    ...mapActions('AuthModule', ['fetchUserTeam', 'fetchUserInvitations', 'fetchUserSubscription', 'fetchAppLimits']),
    ...mapActions('BoardsModule', ['fetchBoards', 'fetchFolders']),
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    ...mapActions('BriefsModule', ['fetchBriefs']),
    async acceptInvite () {
      this.loadingAccept = true
      this.$showAlert({ type: 'info', message: 'Joining team. This may take a few seconds...' })
      try {
        await ForeplayAPI.Teams.respondToInvitation(this.inviteId, 'accepted')
        await CloudFunctionsAPI.Teams.migrateUserToTeam(this.invite.teamId)
        // Reload the Firebase user to pick up backend email verification
        const currentUser = firebase.auth().currentUser
        await currentUser.reload()
        await currentUser.getIdToken(true)
        // Update Vuex store so router guard sees the new state
        this.$store.commit('AuthModule/SET_IS_EMAIL_VERIFIED', currentUser.emailVerified)
        await FirebaseAPI.Users.update(currentUser.uid, { onboardingStatus: 'FINISH' })
        this.$store.commit('AuthModule/SET_USER', {
          ...this.getUser,
          onboardingStatus: 'FINISH'
        })

        // Fetch all necessary data before redirecting to dashboard
        await Promise.all([
          this.fetchUserTeam(),
          this.fetchUserSubscription(true)
        ])
        await this.fetchAppLimits()
        this.fetchBoards()
        this.fetchFolders()
        this.fetchSpyderSubscriptions()
        this.fetchBriefs()
        this.fetchUserInvitations()

        this.$showAlert({
          type: 'success',
          message: 'Team joined successfully.'
        })

        this.$router.push({ name: 'DiscoveryView' })
      } catch (error) {
        console.error(error)
        const errMessage = error.response?.data?.message || error?.message || 'An error occurred while joining the team.'
        this.$showAlert({
          message: errMessage,
          type: 'error'
        })
        this.loadingAccept = false
      }
    },
    async declineInvite () {
      this.loadingDecline = true
      try {
        await ForeplayAPI.Teams.respondToInvitation(this.inviteId, 'rejected')

        this.$showAlert({
          type: 'success',
          message: 'Invitation declined.'
        })
        this.$router.push({ name: 'UserOnboardView' })
      } catch (error) {
        console.error(error)
        const errMessage = error.response?.data?.message || error?.message || 'An error occurred.'
        this.$showAlert({
          message: errMessage,
          type: 'error'
        })
        this.loadingDecline = false
      }
    }
  }
}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}

.submit-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}

.submit-btn:hover:not(:disabled) {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  background: white;
}

.base-btn:hover:not(:disabled) {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.base-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
